import * as React from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  apiClient,
  ApiError,
  compareByFirstName,
  compareByLastName,
  formatPhone,
  splitPersonName,
  describePhotoExclusions,
  parsePhotoExclusions,
  photoWishesFromCodes,
  PHOTO_EXCLUSION_LABELS,
  type AdminChild,
  type Enrolment,
  type ExclusionTag,
  type PaymentStatus as PaymentStatusT,
  type Program,
} from "@alight/contracts";
import {
  Badge,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Textarea,
} from "@alight/ui";
import {
  Archive,
  ArchiveRestore,
  AlertTriangle,
  CalendarClock,
  CameraOff,
  Eye,
  Pencil,
  Tags,
  Trash2,
} from "lucide-react";
import { RowAction, RowActions } from "../components/RowActions";
import { YearLevelReviewDialog } from "../components/YearLevelReviewDialog";
import { downloadCsv } from "./csv";

const API_BASE = "";

/**
 * How the roll is ordered. The server hands the list back by first name, which
 * is how it has always read — that stays the default so nothing moves under a
 * host who never touches this. Surname order is what a class list, a name badge
 * run or a school's own spreadsheet is filed by. The surname is derived from
 * the one name box (see `splitPersonName` in @alight/contracts), so it matches
 * the first/last columns in the registration spreadsheet exactly.
 */
type ChildSort = "child_first" | "child_last" | "parent_last";

const CHILD_SORT_LABELS: Record<ChildSort, string> = {
  child_first: "First name A–Z",
  child_last: "Surname A–Z",
  parent_last: "Parent surname A–Z",
};

/**
 * STUDENTS — every child is a record in its own right (children table), and
 * each enrolment is assigned to one. This page lists the records with their
 * parent's saved details and enrolment aggregates; the detail dialog shows the
 * child's full enrolment history across terms.
 */
export function AdminStudents() {
  const api = React.useMemo(() => apiClient(API_BASE), []);
  const [search, setSearch] = React.useState("");
  const [sort, setSort] = React.useState<ChildSort>("child_first");
  const [showArchived, setShowArchived] = React.useState(false);
  const [viewing, setViewing] = React.useState<AdminChild | null>(null);
  const [editing, setEditing] = React.useState<AdminChild | null>(null);
  const [editingParent, setEditingParent] = React.useState<AdminChild | null>(null);
  const [deleting, setDeleting] = React.useState<AdminChild | null>(null);
  const [yearReviewOpen, setYearReviewOpen] = React.useState(false);
  const [manageTagsOpen, setManageTagsOpen] = React.useState(false);

  const childrenQuery = useQuery({
    queryKey: ["admin", "children"],
    queryFn: () => api.adminListChildren(),
    staleTime: 30 * 1000,
    retry: 1,
  });

  /* One list powers every history dialog (the roster is small enough).
     HISTORY MEANS ALL OF IT: unlike the Submissions inbox, which hides closed
     registrations so they stop being counted, a child's record should still
     show one the family pulled out of — it is the answer to "weren't they in
     last term?". They are labelled Closed below rather than left to read as an
     unpaid registration nobody chased. */
  const enrolmentsQuery = useQuery({
    queryKey: ["admin", "enrolments", "all", "include-closed"],
    queryFn: () => api.listEnrolments({ include_closed: true }),
    staleTime: 30 * 1000,
    retry: 1,
  });

  // Programs give each history row a readable program name (includes past
  // and hidden programs so old enrolments still label correctly).
  const programsQuery = useQuery({
    queryKey: ["admin", "programs"],
    queryFn: () => api.adminListPrograms(),
    staleTime: 5 * 60 * 1000,
    retry: 1,
  });
  const programById = React.useMemo(() => {
    const m = new Map<string, Program>();
    for (const p of programsQuery.data ?? []) m.set(String(p.id), p);
    return m;
  }, [programsQuery.data]);

  /* The host's own "not available for" labels. A child record only stores which
     labels are on it, so the list has to be loaded to turn those into words for
     the table, the edit dialog and the export. It changes only when the host
     adds or renames one, hence the minute of staleness. */
  const tagsQuery = useQuery({
    queryKey: ["admin", "exclusion-tags"],
    queryFn: () => api.adminListExclusionTags(),
    staleTime: 60 * 1000,
    retry: 1,
  });
  const tags = tagsQuery.data ?? [];

  const all = childrenQuery.data ?? [];
  const archivedCount = React.useMemo(
    () => all.filter((ch) => ch.archived_at).length,
    [all],
  );

  const rows = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    const filtered = all
      .filter((ch) => (showArchived ? true : !ch.archived_at))
      .filter((ch) =>
        q === ""
          ? true
          : [
              ch.name,
              ch.year_level ?? "",
              ch.parent_name ?? "",
              ch.parent_email,
              /* Three ways to write one number: as stored, as displayed, and
                 as bare digits. A host typing "0412 345" and a host typing
                 "0412345678" are both looking for the same family. */
              ch.parent_phone ?? "",
              formatPhone(ch.parent_phone),
              String(ch.parent_phone ?? "").replace(/\D/g, ""),
            ]
              .join(" ")
              .toLowerCase()
              .includes(q),
      );
    // Already a fresh array from .filter(), so sorting in place is safe here.
    if (sort === "child_last") filtered.sort((a, b) => compareByLastName(a.name, b.name));
    else if (sort === "parent_last")
      filtered.sort((a, b) => compareByLastName(a.parent_name, b.parent_name));
    else filtered.sort((a, b) => compareByFirstName(a.name, b.name));
    return filtered;
  }, [all, search, showArchived, sort]);

  function exportCsv() {
    downloadCsv(
      "students",
      [
        "id",
        "name",
        // Derived beside the full name (same rule as the registration sheet), so
        // this file can be sorted or mail-merged by surname in a spreadsheet.
        "first_name",
        "last_name",
        "year_level",
        "allergies",
        "photo_consent",
        "photo_exclusions",
        // The host's own "not available for" labels, so the sheet can be
        // filtered on them the same way the screen can.
        "not_available_for",
        "parent_name",
        "parent_first_name",
        "parent_last_name",
        "parent_email",
        "parent_phone",
        "enrolment_count",
        "last_enrolled_at",
        "archived",
        "created_at",
      ],
      rows.map((ch) => ({
        id: ch.id,
        name: ch.name,
        first_name: splitPersonName(ch.name).first,
        last_name: splitPersonName(ch.name).last,
        year_level: ch.year_level ?? "",
        allergies: ch.allergies ?? "",
        photo_consent:
          ch.photo_consent === 1 ? "yes" : ch.photo_consent === 0 ? "no" : "",
        photo_exclusions: describePhotoExclusions(ch.photo_exclusions, ch.photo_exclude_note),
        not_available_for: childExclusionLabels(ch, tags).join(", "),
        parent_name: ch.parent_name ?? "",
        parent_first_name: splitPersonName(ch.parent_name).first,
        parent_last_name: splitPersonName(ch.parent_name).last,
        parent_email: ch.parent_email,
        // Grouped so the spreadsheet keeps the leading 0 — see formatPhone.
        parent_phone: formatPhone(ch.parent_phone),
        enrolment_count: ch.enrolment_count,
        last_enrolled_at: ch.last_enrolled_at ?? "",
        archived: ch.archived_at ? "yes" : "no",
        created_at: ch.created_at,
      })),
    );
  }

  return (
    <section className="space-y-6">
      <header>
        <h2 className="font-heading text-xl font-bold text-ink">Children</h2>
        <p className="mt-1 text-sm text-muted">
          Every child on record, with their family&apos;s details and enrolment history.
        </p>
      </header>

      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <Input
          type="search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search child, parent, email…"
          className="sm:max-w-xs"
          aria-label="Search children"
        />
        <div className="flex flex-wrap items-center gap-3">
          <Select value={sort} onValueChange={(v) => setSort(v as ChildSort)}>
            <SelectTrigger className="w-44" aria-label="Sort children">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(Object.keys(CHILD_SORT_LABELS) as ChildSort[]).map((key) => (
                <SelectItem key={key} value={key}>
                  {CHILD_SORT_LABELS[key]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {archivedCount > 0 ? (
            <Button
              type="button"
              variant={showArchived ? "default" : "outline"}
              size="sm"
              onClick={() => setShowArchived((v) => !v)}
              aria-pressed={showArchived}
            >
              {showArchived ? "Hide archived" : `Show archived (${archivedCount})`}
            </Button>
          ) : null}
          <span className="text-sm text-muted">{rows.length} shown</span>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setYearReviewOpen(true)}
            disabled={all.length === 0}
          >
            <CalendarClock className="h-4 w-4" />
            Start-of-year review
          </Button>
          {/* Not disabled when the list is empty — an empty list is exactly when
              the host needs to get in and make their first label. */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => setManageTagsOpen(true)}
            title="Add or change the labels you tick on a child, like “Term 4 sport”"
          >
            <Tags className="h-4 w-4" />
            Manage tags
          </Button>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={exportCsv}
            disabled={rows.length === 0}
          >
            Export CSV
          </Button>
        </div>
      </div>

      {childrenQuery.isLoading ? (
        <div className="rounded-card border border-border bg-paper px-4 py-8 text-center text-sm text-muted">
          Loading…
        </div>
      ) : childrenQuery.error ? (
        <div
          role="alert"
          className="rounded-card border border-error bg-error/10 px-4 py-3 text-sm text-ink"
        >
          Couldn&apos;t load children
          {childrenQuery.error instanceof ApiError ? ` (${childrenQuery.error.status})` : ""}.
          Please retry.
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-card border border-border bg-paper px-4 py-8 text-center text-sm text-muted">
          No children match the current search.
        </div>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Child</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead>Medical / photo</TableHead>
                  {/* Its own column rather than more chips in Medical / photo:
                      that cell is what the PARENT asked for and a helper has to
                      honour, this one is the host's own note about who is
                      already busy. Reading them as one list would invite
                      treating "Term 4 sport" as a care instruction. */}
                  <TableHead>Not available for</TableHead>
                  <TableHead>Parent / guardian</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Enrolments</TableHead>
                  <TableHead>Last enrolled</TableHead>
                  <TableHead className="sticky right-0 z-10 bg-card text-right">
                    Actions
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((ch) => (
                  <TableRow key={ch.id} className={ch.archived_at ? "opacity-60" : undefined}>
                    <TableCell className="font-medium text-ink">
                      <span className="inline-flex items-center gap-2">
                        {ch.name}
                        {ch.archived_at ? (
                          <Badge variant="muted" className="font-normal">
                            Archived
                          </Badge>
                        ) : null}
                      </span>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink">
                      {ch.year_level ?? "—"}
                    </TableCell>
                    <TableCell>
                      <MedicalPhotoCell child={ch} />
                    </TableCell>
                    <TableCell>
                      <ExclusionTagsCell child={ch} tags={tags} />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-start gap-1">
                        <div className="min-w-0">
                          <div className="font-medium text-ink">{ch.parent_name ?? "—"}</div>
                          <div className="break-all text-xs text-muted">{ch.parent_email}</div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 flex-shrink-0"
                          aria-label={`Edit contact details for ${ch.parent_name ?? ch.parent_email}`}
                          title="Edit the family's contact details"
                          onClick={() => setEditingParent(ch)}
                        >
                          <Pencil className="h-3.5 w-3.5" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="whitespace-nowrap text-ink">
                      {/* Grouped for reading, and the same way the export
                          writes it — see formatPhone. */}
                      {formatPhone(ch.parent_phone) || "—"}
                    </TableCell>
                    <TableCell className="text-ink">{ch.enrolment_count}</TableCell>
                    <TableCell className="whitespace-nowrap text-muted">
                      {ch.last_enrolled_at ? formatDay(ch.last_enrolled_at) : "—"}
                    </TableCell>
                    {/* Named actions in a menu, matching the Submissions table:
                        an icon alone made the host click a button to learn what
                        it did, and one of these erases a student outright — but
                        four labelled buttons wrapped and made every child three
                        lines tall. The menu keeps the words and the note naming
                        the consequence. See components/RowActions. */}
                    <TableCell className="sticky right-0 z-10 bg-card text-right">
                      <RowActions label={`Actions for ${ch.name}`}>
                        <RowAction
                          icon={Eye}
                          label="View"
                          description="See this student's details, medical notes and enrolment history."
                          onSelect={() => setViewing(ch)}
                        />
                        <RowAction
                          icon={Pencil}
                          label="Edit"
                          description="Fix this student's name, year level or details. Nobody is emailed."
                          onSelect={() => setEditing(ch)}
                        />
                        <ArchiveAction api={api} child={ch} />
                        <RowAction
                          icon={Trash2}
                          label="Delete"
                          description="Permanently erase this student and every registration, payment and roll entry attached to them. Cannot be undone — use Archive to simply hide them."
                          destructive
                          onSelect={() => setDeleting(ch)}
                        />
                      </RowActions>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      <StudentDetailsDialog
        key={viewing?.id ?? "none-view"}
        child={viewing}
        enrolments={(enrolmentsQuery.data ?? []).filter((e) => e.child_id === viewing?.id)}
        enrolmentsLoading={enrolmentsQuery.isLoading}
        programById={programById}
        onClose={() => setViewing(null)}
      />

      <EditStudentDialog
        key={editing?.id ?? "none"}
        api={api}
        child={editing}
        tags={tags}
        onClose={() => setEditing(null)}
      />

      <YearLevelReviewDialog
        api={api}
        open={yearReviewOpen}
        onOpenChange={setYearReviewOpen}
        students={all.filter((ch) => !ch.archived_at)}
      />

      <EditParentDialog
        key={editingParent?.parent_email ?? "none-parent"}
        api={api}
        child={editingParent}
        onClose={() => setEditingParent(null)}
      />

      <DeleteStudentDialog
        key={deleting?.id ?? "none-del"}
        api={api}
        child={deleting}
        onClose={() => setDeleting(null)}
      />

      <ManageExclusionTagsDialog
        api={api}
        open={manageTagsOpen}
        onOpenChange={setManageTagsOpen}
        tags={tags}
        loading={tagsQuery.isLoading}
        loadError={tagsQuery.error}
      />
    </section>
  );
}

/** Allergy warning + photo-opt-out indicators for the children table.
 *  The photo badge says WHAT the parent excluded ("No photos: social media /
 *  website"), because "photos are fine, just not on Instagram" is a different
 *  instruction to a helper than a blanket no. Children whose consent predates
 *  the specific questions still only have the coarse flag — they fall back to
 *  the old wording. */
function MedicalPhotoCell({ child }: { child: AdminChild }) {
  const photoNote =
    describePhotoExclusions(child.photo_exclusions, child.photo_exclude_note) ||
    (child.photo_consent === 0 ? "No photos" : "");
  if (!child.allergies && photoNote === "") {
    return <span className="text-muted">—</span>;
  }
  return (
    <div className="flex flex-col items-start gap-1">
      {child.allergies ? (
        <span
          title={`Medical / allergies: ${child.allergies}`}
          style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
        >
          <AlertTriangle className="h-3 w-3 flex-shrink-0" aria-hidden />
          <span className="truncate">{child.allergies}</span>
        </span>
      ) : null}
      {photoNote ? (
        <span
          title={photoNote}
          style={{ backgroundColor: "#e2e8f0", color: "#334155" }}
          className="inline-flex max-w-[16rem] items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold"
        >
          <CameraOff className="h-3 w-3 flex-shrink-0" aria-hidden />
          <span className="truncate">{photoNote}</span>
        </span>
      ) : null}
    </div>
  );
}

/**
 * The labels currently on a child, as words.
 *
 * Walks the host's own list rather than the child's stored order, so the chips
 * on a row, the ticks in the edit dialog and the column in the export all read
 * in the same order. A label the host deleted while this page sat open can
 * still be listed on a stale row — it drops out silently, which beats a blank
 * chip nobody can explain.
 */
function childExclusionLabels(child: AdminChild, tags: ExclusionTag[]): string[] {
  const on = new Set(child.exclusions);
  return tags.filter((t) => on.has(t.id)).map((t) => t.label);
}

/** The host's "not available for" labels on one child row.
 *  Only the first two get their own chip: a child with four commitments turned
 *  the row into a paragraph, and the host scanning this column wants to know
 *  THAT someone is busy before exactly what with. The rest are counted, and the
 *  tooltip spells out the full list for when they do want it. */
function ExclusionTagsCell({ child, tags }: { child: AdminChild; tags: ExclusionTag[] }) {
  const labels = childExclusionLabels(child, tags);
  if (labels.length === 0) return <span className="text-muted">—</span>;
  const shown = labels.slice(0, 2);
  const hidden = labels.length - shown.length;
  const all = labels.join(", ");
  return (
    <div className="flex flex-wrap items-center gap-1" title={`Not available for: ${all}`}>
      {shown.map((label) => (
        <Badge key={label} variant="muted" className="max-w-[10rem] truncate font-normal">
          {label}
        </Badge>
      ))}
      {hidden > 0 ? (
        <Badge variant="muted" className="font-normal">
          +{hidden}
        </Badge>
      ) : null}
    </div>
  );
}

function EditStudentDialog({
  api,
  child,
  tags,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  child: AdminChild | null;
  /** The host's own labels, to offer as ticks. Empty until they make one. */
  tags: ExclusionTag[];
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = React.useState(child?.name ?? "");
  const [yearLevel, setYearLevel] = React.useState(child?.year_level ?? "");
  const [allergies, setAllergies] = React.useState(child?.allergies ?? "");
  /* Photo permission mirrors the ENROLMENT FORM: the parent grants everything
   * unless they tick an exclusion, so the host edits the same exclusions here
   * rather than a yes/no that can't express "not on social media". A child
   * whose record predates the specific questions and was marked "no" starts
   * with every box ticked, so nothing silently becomes a yes. */
  const legacyBlanketNo =
    child?.photo_consent === 0 &&
    parsePhotoExclusions(child?.photo_exclusions).length === 0 &&
    !child?.photo_exclude_note;
  const [exclusions, setExclusions] = React.useState<string[]>(
    legacyBlanketNo
      ? Object.keys(PHOTO_EXCLUSION_LABELS)
      : parsePhotoExclusions(child?.photo_exclusions),
  );
  const [photoNote, setPhotoNote] = React.useState(child?.photo_exclude_note ?? "");
  /* The host's own labels, kept apart from the photo codes above: both are
     "exclusions" to the host, but one is the family's instruction about photos
     and this one is the host's note that the child is already committed
     elsewhere. Named for the question it answers to keep the two straight. */
  const [notAvailableFor, setNotAvailableFor] = React.useState<number[]>(
    child?.exclusions ?? [],
  );

  function toggleExclusion(code: string, on: boolean) {
    setExclusions((prev) =>
      on ? [...prev.filter((c) => c !== code), code] : prev.filter((c) => c !== code),
    );
  }

  function toggleNotAvailable(id: number, on: boolean) {
    setNotAvailableFor((prev) =>
      on ? [...prev.filter((t) => t !== id), id] : prev.filter((t) => t !== id),
    );
  }

  const save = useMutation({
    mutationFn: () =>
      api.adminUpdateChild(child!.id, {
        name: name.trim(),
        year_level: yearLevel.trim() === "" ? null : yearLevel.trim(),
        allergies: allergies.trim() === "" ? null : allergies.trim(),
        // Same helper the enrolment form's answers go through, so a host edit
        // and a parent's own answer can never disagree about what's stored.
        ...photoWishesFromCodes(exclusions, photoNote),
        // Sent whole — this list REPLACES whatever was ticked before, so
        // unticking a box is simply a shorter list.
        exclusions: notAvailableFor,
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      // The attendance roll reads the same child records.
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  return (
    <Dialog open={child !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      {/* Scrolls like the details dialog: a host with a handful of their own
          labels pushed Save below the fold, and an unreachable Save button
          reads as a broken form. */}
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{child?.name ?? "Child"}</DialogTitle>
          <DialogDescription>
            Update this child&apos;s name, year level, medical/allergy note, photo permission and
            what they&apos;re not available for. These apply across every term and show on the
            attendance roll.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="edit-name">Child&apos;s name</Label>
            <Input
              id="edit-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name as it should appear on the roll"
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-year">Year level</Label>
            <Input
              id="edit-year"
              value={yearLevel}
              onChange={(e) => setYearLevel(e.target.value)}
              placeholder="Year 4"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="edit-allergies">Medical / allergies</Label>
            <Textarea
              id="edit-allergies"
              value={allergies}
              onChange={(e) => setAllergies(e.target.value)}
              placeholder="e.g. Peanut allergy — carries EpiPen. Leave blank if none."
              rows={3}
            />
          </div>

          <div className="space-y-1.5">
            <Label>Photo permission</Label>
            <p className="text-xs text-muted">
              Tick only what this family does NOT permit. Nothing ticked means photos are fine.
            </p>
            <div className="space-y-2 pt-1">
              {Object.entries(PHOTO_EXCLUSION_LABELS).map(([code, label]) => (
                <label key={code} className="flex items-start gap-2 text-sm text-ink">
                  <input
                    type="checkbox"
                    className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                    checked={exclusions.includes(code)}
                    onChange={(e) => toggleExclusion(code, e.target.checked)}
                  />
                  <span>No photos/videos for {label}</span>
                </label>
              ))}
            </div>
            <Input
              id="edit-photo-note"
              value={photoNote}
              onChange={(e) => setPhotoNote(e.target.value)}
              placeholder="Any other photo restriction (optional)"
            />
          </div>

          <div className="space-y-1.5">
            <Label>Not available for</Label>
            <p className="text-xs text-muted">
              Tick anything this child is already committed to. They&apos;ll be left out of
              messages about it, and out of the list of children free to enrol.
            </p>
            {tags.length === 0 ? (
              /* Nothing to tick yet. Said here rather than left as an empty gap,
                 because the host's next question is "where do I make one?". */
              <p className="rounded-card border border-border bg-paper px-3 py-2 text-xs text-muted">
                You haven&apos;t made any labels yet. Use <strong>Manage tags</strong> on the
                children list to add one, like &ldquo;Term 4 sport&rdquo;.
              </p>
            ) : (
              <div className="space-y-2 pt-1">
                {tags.map((tag) => (
                  <label key={tag.id} className="flex items-start gap-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      className="mt-0.5 h-4 w-4 rounded border-border text-primary focus:ring-primary"
                      checked={notAvailableFor.includes(tag.id)}
                      onChange={(e) => toggleNotAvailable(tag.id, e.target.checked)}
                    />
                    <span>{tag.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>

          {save.error ? (
            <p className="text-sm text-accent-fg" role="alert">
              {save.error instanceof ApiError ? save.error.message : "Couldn’t save — please retry."}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || name.trim() === ""}>
              {save.isPending ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Prefer the server's own sentence.
 *
 * The one refusal the host will actually meet here is a duplicate label — the
 * server answers that with a readable explanation naming the label they already
 * have, and swallowing it for "Couldn't save" would leave them typing the same
 * thing again. See the ApiError construction in @alight/contracts.
 */
function apiErrorText(error: unknown, fallback: string): string {
  return error instanceof ApiError ? error.message : fallback;
}

/** "5 children" / "1 child" / "no children yet" — the host's reason to keep or
 *  drop a label, and the thing a delete is about to undo. */
function tagChildCountLabel(count: number): string {
  if (count === 0) return "no children yet";
  return count === 1 ? "1 child" : `${count} children`;
}

/**
 * Make, rename and delete the host's own "not available for" labels.
 *
 * These are rows, not code, precisely so the host never has to ask a developer
 * for "Term 4 sport" — so the whole lifecycle has to live on this screen. Kept
 * to one small dialog off the toolbar rather than a page of its own: it is a
 * once-a-term visit, and it belongs next to the children it labels.
 */
function ManageExclusionTagsDialog({
  api,
  open,
  onOpenChange,
  tags,
  loading,
  loadError,
}: {
  api: ReturnType<typeof apiClient>;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tags: ExclusionTag[];
  loading: boolean;
  loadError: unknown;
}) {
  const qc = useQueryClient();
  const [newLabel, setNewLabel] = React.useState("");
  const [renamingId, setRenamingId] = React.useState<number | null>(null);
  const [renameLabel, setRenameLabel] = React.useState("");
  const [confirmingId, setConfirmingId] = React.useState<number | null>(null);

  /* Both lists move together. The labels themselves are obvious; the children
     are the one that gets forgotten — deleting a label takes it off every
     child, so their rows are showing something that no longer exists until
     they are refetched too. */
  function refreshBothLists() {
    qc.invalidateQueries({ queryKey: ["admin", "exclusion-tags"] });
    qc.invalidateQueries({ queryKey: ["admin", "children"] });
  }

  const create = useMutation({
    // New labels land at the end of the host's list rather than jumping into
    // the middle of a group they already read in a particular order.
    mutationFn: (label: string) =>
      api.adminSaveExclusionTag({ label, sort_order: tags.length }),
    onSuccess: () => {
      refreshBothLists();
      setNewLabel("");
    },
  });

  const rename = useMutation({
    mutationFn: (tag: { id: number; label: string; sort_order: number }) =>
      api.adminSaveExclusionTag({ label: tag.label, sort_order: tag.sort_order }, tag.id),
    onSuccess: () => {
      refreshBothLists();
      setRenamingId(null);
    },
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.adminDeleteExclusionTag(id),
    onSuccess: () => {
      refreshBothLists();
      setConfirmingId(null);
    },
  });

  const busy = create.isPending || rename.isPending || remove.isPending;

  function handleOpenChange(next: boolean) {
    /* Closing abandons whatever was half-done. A rename box still open, or a
       "are you sure?" still showing, would greet the host on their next visit
       as a question they thought they had walked away from. */
    if (!next) {
      setRenamingId(null);
      setConfirmingId(null);
      setNewLabel("");
      create.reset();
      rename.reset();
      remove.reset();
    }
    onOpenChange(next);
  }

  function startRename(tag: ExclusionTag) {
    rename.reset();
    setConfirmingId(null);
    setRenamingId(tag.id);
    setRenameLabel(tag.label);
  }

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Exclusion labels</DialogTitle>
          <DialogDescription>
            Your own labels for things a child is already committed to. Tick one on a child and
            they&apos;ll be left out of messages about it, and out of the list of children free to
            enrol. Nothing here emails anyone.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <p className="text-sm text-muted">Loading…</p>
        ) : loadError ? (
          <p
            role="alert"
            className="rounded-card border border-error bg-error/10 px-3 py-2 text-sm text-ink"
          >
            {apiErrorText(loadError, "Couldn’t load your labels — please retry.")}
          </p>
        ) : tags.length === 0 ? (
          <p className="rounded-card border border-border bg-paper px-3 py-3 text-sm text-muted">
            No labels yet. Make one like &ldquo;Term 4 sport&rdquo; and tick it on any child
            who&apos;s already committed — they&apos;ll be left out of messages about it.
          </p>
        ) : (
          <ul className="divide-y divide-border rounded-card border border-border">
            {tags.map((tag) => (
              <li key={tag.id} className="px-3 py-2">
                {renamingId === tag.id ? (
                  <form
                    className="space-y-2"
                    onSubmit={(e) => {
                      e.preventDefault();
                      rename.mutate({
                        id: tag.id,
                        label: renameLabel.trim(),
                        sort_order: tag.sort_order,
                      });
                    }}
                  >
                    <Label htmlFor={`rename-tag-${tag.id}`} className="text-xs">
                      New wording for this label
                    </Label>
                    <Input
                      id={`rename-tag-${tag.id}`}
                      value={renameLabel}
                      onChange={(e) => setRenameLabel(e.target.value)}
                      maxLength={60}
                      autoFocus
                    />
                    {/* Renaming only changes the words. Said out loud because the
                        host's fear is that fixing a typo unticks everybody. */}
                    <p className="text-xs text-muted">
                      Every child already ticked keeps this label — only the wording changes.
                    </p>
                    {rename.error ? (
                      <p className="text-sm text-accent-fg" role="alert">
                        {apiErrorText(rename.error, "Couldn’t rename it — please retry.")}
                      </p>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        type="submit"
                        size="sm"
                        disabled={
                          rename.isPending ||
                          renameLabel.trim() === "" ||
                          renameLabel.trim() === tag.label
                        }
                      >
                        {rename.isPending ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={rename.isPending}
                        onClick={() => setRenamingId(null)}
                      >
                        Cancel
                      </Button>
                    </div>
                  </form>
                ) : confirmingId === tag.id ? (
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-ink">
                      Delete &ldquo;{tag.label}&rdquo;?
                    </p>
                    {/* The host's real question is "do I lose the children?".
                        Answered before they have to click to find out. */}
                    <p className="text-xs text-muted">
                      This removes the label from the list and takes it off{" "}
                      {tagChildCountLabel(tag.child_count)}. The children themselves are not
                      changed and nothing is emailed — they just stop being left out of things.
                    </p>
                    {remove.error ? (
                      <p className="text-sm text-accent-fg" role="alert">
                        {apiErrorText(remove.error, "Couldn’t delete it — please retry.")}
                      </p>
                    ) : null}
                    <div className="flex gap-2">
                      <Button
                        type="button"
                        variant="accent"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => remove.mutate(tag.id)}
                      >
                        {remove.isPending ? "Deleting…" : "Yes, delete it"}
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        disabled={remove.isPending}
                        onClick={() => setConfirmingId(null)}
                      >
                        Keep it
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium text-ink">{tag.label}</div>
                      <div className="text-xs text-muted">
                        {tagChildCountLabel(tag.child_count)}
                      </div>
                    </div>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      aria-label={`Rename ${tag.label}`}
                      title="Change the wording of this label"
                      disabled={busy}
                      onClick={() => startRename(tag)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      aria-label={`Delete ${tag.label}`}
                      title="Delete this label"
                      disabled={busy}
                      onClick={() => {
                        remove.reset();
                        setRenamingId(null);
                        setConfirmingId(tag.id);
                      }}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                )}
              </li>
            ))}
          </ul>
        )}

        <form
          className="space-y-1.5"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate(newLabel.trim());
          }}
        >
          <Label htmlFor="new-exclusion-tag">Add a label</Label>
          <div className="flex gap-2">
            <Input
              id="new-exclusion-tag"
              value={newLabel}
              onChange={(e) => setNewLabel(e.target.value)}
              placeholder="e.g. Term 4 sport"
              maxLength={60}
            />
            <Button
              type="submit"
              size="sm"
              className="flex-shrink-0"
              disabled={create.isPending || newLabel.trim() === ""}
            >
              {create.isPending ? "Adding…" : "Add"}
            </Button>
          </div>
          {create.error ? (
            <p className="text-sm text-accent-fg" role="alert">
              {apiErrorText(create.error, "Couldn’t add it — please retry.")}
            </p>
          ) : null}
        </form>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
            Done
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * Correct a family's contact details.
 *
 * All three are the admin's to correct, the email included: it used to sit
 * behind an extra "Change email" unlock, which mostly just made a routine typo
 * fix feel forbidden.
 *
 * The email is still not quite an ordinary text box, though — it is how the
 * parent signs in, where every receipt and reminder goes, and the key tying
 * their children and enrolments together, so changing it moves all of those
 * records onto the new address. That is right when it is a typo and wrong when
 * it is a different person. So the field is freely editable and the warning
 * appears the moment the address actually DIFFERS: the consequence is stated
 * when it applies, instead of a click standing in the way of every visit.
 */
function EditParentDialog({
  api,
  child,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  child: AdminChild | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const originalEmail = child?.parent_email ?? "";
  const [name, setName] = React.useState(child?.parent_name ?? "");
  const [phone, setPhone] = React.useState(child?.parent_phone ?? "");
  const [email, setEmail] = React.useState(originalEmail);

  const emailChanged = email.trim().toLowerCase() !== originalEmail.toLowerCase();

  const save = useMutation({
    mutationFn: () =>
      api.adminUpdateParent(originalEmail, {
        name: name.trim(),
        phone: phone.trim() === "" ? null : phone.trim(),
        ...(emailChanged ? { email: email.trim().toLowerCase() } : {}),
      }),
    onSuccess: () => {
      // The family's details are stamped on the children list, the submissions
      // inbox and the attendance roll — refresh all three.
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  return (
    <Dialog open={child !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{child?.parent_name || "Parent / guardian"}</DialogTitle>
          <DialogDescription>
            These details apply to the whole family — every child and every enrolment under this
            address.
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            save.mutate();
          }}
          className="space-y-4"
        >
          <div className="space-y-1.5">
            <Label htmlFor="parent-name">Parent / guardian name</Label>
            <Input
              id="parent-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="parent-phone">Phone</Label>
            <Input
              id="parent-phone"
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Leave blank if you don't have one"
            />
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="parent-email">Email address</Label>
            <Input
              id="parent-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
            {emailChanged ? (
              <div
                role="note"
                className="flex items-start gap-2 rounded-card border border-accent bg-accent/10 px-3 py-2 text-xs text-ink"
              >
                <AlertTriangle className="mt-0.5 h-4 w-4 flex-shrink-0 text-accent-fg" aria-hidden />
                <div className="space-y-1">
                  <p className="font-semibold">Please double-check this address.</p>
                  <p>
                    It is how this parent signs in, and where their receipts and reminders are
                    sent. Changing it moves {child ? childCountLabel(child) : "their records"} onto
                    the new address, and the old one stops working straight away. A typo here means
                    the family can no longer reach their enrolment.
                  </p>
                </div>
              </div>
            ) : null}
          </div>

          {save.error ? (
            <p className="text-sm text-accent-fg" role="alert">
              {save.error instanceof ApiError
                ? save.error.message
                : "Couldn’t save — please retry."}
            </p>
          ) : null}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>
              Cancel
            </Button>
            <Button type="submit" disabled={save.isPending || name.trim() === ""}>
              {save.isPending
                ? "Saving…"
                : emailChanged
                  ? "Save and move their records"
                  : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

/** "their 2 enrolments" / "their child and 3 enrolments" — spells out what a
 *  re-key actually touches, in the host's language rather than table names. */
function childCountLabel(child: AdminChild): string {
  const enrolments = child.enrolment_count;
  const enrolmentPart =
    enrolments === 1 ? "1 enrolment" : `${enrolments} enrolments`;
  return `this family's records (${enrolmentPart})`;
}

/** Archive / restore toggle for a single child row. Archiving is reversible
 *  and keeps the record + email; it just drops the child from the active list. */
function ArchiveAction({
  api,
  child,
}: {
  api: ReturnType<typeof apiClient>;
  child: AdminChild;
}) {
  const qc = useQueryClient();
  const isArchived = Boolean(child.archived_at);
  const toggle = useMutation({
    mutationFn: () => api.adminArchiveChild(child.id, !isArchived),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
    },
  });
  return isArchived ? (
    <RowAction
      icon={ArchiveRestore}
      label="Restore"
      description="Put this student back in the active list. Nothing is emailed."
      disabled={toggle.isPending}
      onSelect={() => toggle.mutate()}
    />
  ) : (
    <RowAction
      icon={Archive}
      label="Archive"
      description="Hide this student from the list without deleting anything — their registrations and history are kept, and you can restore them at any time. Nothing is emailed."
      disabled={toggle.isPending}
      onSelect={() => toggle.mutate()}
    />
  );
}

/** Permanent-delete confirmation. Guarded by a typed "DELETE" so a mis-click on
 *  a real family can't wipe their record + enrolment/payment history. Intended
 *  for test data; archiving is the reversible path offered alongside it. */
function DeleteStudentDialog({
  api,
  child,
  onClose,
}: {
  api: ReturnType<typeof apiClient>;
  child: AdminChild | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [confirm, setConfirm] = React.useState("");

  const del = useMutation({
    mutationFn: () => api.adminDeleteChild(child!.id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "children"] });
      qc.invalidateQueries({ queryKey: ["admin", "enrolments"] });
      qc.invalidateQueries({ queryKey: ["admin", "att"] });
      onClose();
    },
  });

  const enrolments = child?.enrolment_count ?? 0;
  const canDelete = confirm.trim().toUpperCase() === "DELETE";

  return (
    <Dialog open={child !== null} onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Delete {child?.name}?</DialogTitle>
          <DialogDescription>
            This permanently removes this child record
            {enrolments > 0
              ? ` and its ${enrolments} enrolment${enrolments === 1 ? "" : "s"} (with any attendance and payment history)`
              : ""}
            . This cannot be undone. To keep the family reachable for email blasts,
            use Archive instead.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1.5">
          <Label htmlFor="delete-confirm">
            Type <span className="font-mono font-semibold">DELETE</span> to confirm
          </Label>
          <Input
            id="delete-confirm"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder="DELETE"
            autoComplete="off"
          />
        </div>

        {del.error ? (
          <p className="text-sm text-accent-fg" role="alert">
            {del.error instanceof ApiError ? del.error.message : "Couldn’t delete — please retry."}
          </p>
        ) : null}

        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose} disabled={del.isPending}>
            Cancel
          </Button>
          <Button
            type="button"
            variant="accent"
            disabled={!canDelete || del.isPending}
            onClick={() => del.mutate()}
          >
            {del.isPending ? "Deleting…" : "Delete permanently"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function StudentDetailsDialog({
  child,
  enrolments,
  enrolmentsLoading,
  programById,
  onClose,
}: {
  /** Null when no row is being viewed — the dialog opens from the row menu. */
  child: AdminChild | null;
  enrolments: Enrolment[];
  enrolmentsLoading: boolean;
  programById: Map<string, Program>;
  onClose: () => void;
}) {
  const programName = (id: string | null | undefined): string => {
    if (id === null || id === undefined || id === "") return "—";
    return programById.get(id)?.title ?? `Program #${id}`;
  };
  if (child === null) return null;
  return (
    <Dialog open onOpenChange={(o) => (!o ? onClose() : undefined)}>
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{child.name}</DialogTitle>
          <DialogDescription>
            Child record #{child.id} — {child.year_level ?? "year level not set"}
          </DialogDescription>
        </DialogHeader>

        <dl className="grid grid-cols-1 gap-x-6 gap-y-2 sm:grid-cols-2">
          <DetailItem label="Parent / guardian" value={child.parent_name ?? "—"} />
          <DetailItem label="Parent email" value={child.parent_email} />
          <DetailItem label="Parent phone" value={formatPhone(child.parent_phone) || "—"} />
          <DetailItem label="First on record" value={formatDate(child.created_at)} />
          <DetailItem label="Medical / allergies" value={child.allergies ?? "None recorded"} />
          <DetailItem
            label="Photo permission"
            value={
              describePhotoExclusions(child.photo_exclusions, child.photo_exclude_note) ||
              (child.photo_consent === 1
                ? "Yes — photos allowed"
                : child.photo_consent === 0
                  ? "No — do not photograph"
                  : "Not answered")
            }
          />
        </dl>

        <div className="space-y-1.5">
          <div className="text-xs font-semibold uppercase tracking-wide text-muted">
            Enrolment history
          </div>
          {enrolmentsLoading ? (
            <p className="text-sm text-muted">Loading…</p>
          ) : enrolments.length === 0 ? (
            <p className="rounded-card border border-border bg-paper px-3 py-2 text-sm text-muted">
              No enrolments assigned to this record.
            </p>
          ) : (
            <div className="overflow-x-auto rounded-card border border-border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Received</TableHead>
                    <TableHead>Program</TableHead>
                    <TableHead>Ref</TableHead>
                    <TableHead>Year</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Payment</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {enrolments.map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="whitespace-nowrap text-muted">
                        {formatDate(e.created_at)}
                      </TableCell>
                      <TableCell>
                        <span
                          className="block max-w-[11rem] truncate text-sm text-ink"
                          title={programName(e.program_id)}
                        >
                          {programName(e.program_id)}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs text-ink">
                        {e.payment_ref}
                      </TableCell>
                      <TableCell className="whitespace-nowrap text-ink">{e.year_level}</TableCell>
                      <TableCell className="whitespace-nowrap text-ink">
                        ${(e.amount_cents / 100).toFixed(2)}
                      </TableCell>
                      <TableCell>
                        {/* A closed registration showing its payment badge read
                            as "Unpaid" and got chased. What matters about it is
                            that it is closed — same rule as the inbox. */}
                        {e.status === "withdrawn" ? (
                          <Badge variant="muted" title="Closed — no longer counted">
                            Closed
                          </Badge>
                        ) : (
                          <Badge variant={statusVariant(e.payment_status)}>
                            {statusLabel(e.payment_status)}
                          </Badge>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

function DetailItem({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col">
      <dt className="text-xs font-semibold uppercase tracking-wide text-muted">{label}</dt>
      <dd className="break-words text-sm text-ink">{value}</dd>
    </div>
  );
}

function statusLabel(s: PaymentStatusT): string {
  switch (s) {
    case "pending_review":
      return "Pending review";
    case "unpaid":
      return "Unpaid";
    case "partial":
      return "Partially paid";
    case "paid":
      return "Paid";
    case "refunded":
      return "Refunded";
    case "cancelled":
      return "Cancelled";
    default:
      return s;
  }
}

function statusVariant(
  s: PaymentStatusT,
): "default" | "accent" | "success" | "muted" | "outline" {
  switch (s) {
    case "paid":
      return "success";
    case "pending_review":
      return "accent";
    case "unpaid":
      return "muted";
    default:
      return "outline";
  }
}

function formatDate(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Date only — keeps grid rows narrow; details dialogs show the full timestamp. */
function formatDay(iso: string): string {
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

